import type { WingDefinition, WingStation } from "./api";

export type PlanformField =
  | "halfSpan"
  | "rootChord"
  | "tipChord"
  | "sweepDeg"
  | "dihedralDeg"
  | "tipTwistDeg";

export interface PlanformShape {
  halfSpan: number;
  rootChord: number;
  tipChord: number;
  sweepDeg: number;
  dihedralDeg: number;
  tipTwistDeg: number;
}

export interface Naca4Shape {
  camberPercent: number;
  camberPositionTenths: number;
  thicknessPercent: number;
}

export interface SectionCampaignControls {
  baseDesignPoints: number;
  camberMinPercent: number;
  camberMaxPercent: number;
  camberPositionMinXc: number;
  camberPositionMaxXc: number;
  thicknessMinPercent: number;
  thicknessMaxPercent: number;
  alphaMin: number;
  alphaMax: number;
  alphaSamples: number;
  reynoldsMin: number;
  reynoldsMax: number;
  machMin: number;
  machMax: number;
}

export interface SectionCampaignGroup {
  id: string;
  designIndex: number;
  alphaValues: number[];
  rowCount: number;
}

export interface HaltonBatchSummary {
  firstGroup: number;
  lastGroup: number;
  groupCount: number;
  rowCount: number;
}

export interface SectionCampaignPlan {
  alphaValues: number[];
  groups: SectionCampaignGroup[];
  haltonBatches: HaltonBatchSummary[];
  groupedAlphaSweeps: number;
  estimatedRows: number;
}

export const DEFAULT_SECTION_CAMPAIGN: SectionCampaignControls = {
  baseDesignPoints: 60,
  camberMinPercent: 0,
  camberMaxPercent: 4,
  camberPositionMinXc: 0.2,
  camberPositionMaxXc: 0.6,
  thicknessMinPercent: 8,
  thicknessMaxPercent: 18,
  alphaMin: -4,
  alphaMax: 18,
  alphaSamples: 12,
  reynoldsMin: 500_000,
  reynoldsMax: 6_000_000,
  machMin: 0.05,
  machMax: 0.3,
};

const degrees = (radians: number): number => radians * 180 / Math.PI;
const radians = (angle: number): number => angle * Math.PI / 180;
const finite = (value: number, fallback: number): number => Number.isFinite(value) ? value : fallback;

function replaceStation(wing: WingDefinition, id: string, replacement: WingStation): WingDefinition {
  return {
    ...wing,
    stations: wing.stations.map((station) => station.id === id ? replacement : { ...station }),
  };
}

export function planformShape(wing: WingDefinition): PlanformShape {
  const root = wing.stations[0]!;
  const tip = wing.stations[wing.stations.length - 1]!;
  const halfSpan = Math.max(1e-9, tip.y - root.y);
  return {
    halfSpan,
    rootChord: root.chord,
    tipChord: tip.chord,
    sweepDeg: degrees(Math.atan2(tip.x_le - root.x_le, halfSpan)),
    dihedralDeg: degrees(Math.atan2(tip.z_le - root.z_le, halfSpan)),
    tipTwistDeg: tip.twist_deg,
  };
}

/** Apply one VSP-like planform knob without mutating the saved wing draft. */
export function setPlanformField(
  wing: WingDefinition,
  field: PlanformField,
  rawValue: number,
): WingDefinition {
  const value = finite(rawValue, planformShape(wing)[field]);
  const root = wing.stations[0]!;
  const tip = wing.stations[wing.stations.length - 1]!;
  const oldSpan = Math.max(1e-9, tip.y - root.y);

  if (field === "rootChord") {
    return replaceStation(wing, root.id, { ...root, chord: Math.max(0.05, value) });
  }
  if (field === "tipChord") {
    return replaceStation(wing, tip.id, { ...tip, chord: Math.max(0.05, value) });
  }

  if (field === "halfSpan") {
    const nextSpan = Math.max(0.1, value);
    const scale = nextSpan / oldSpan;
    return {
      ...wing,
      stations: wing.stations.map((station) => ({
        ...station,
        y: root.y + (station.y - root.y) * scale,
        x_le: root.x_le + (station.x_le - root.x_le) * scale,
        z_le: root.z_le + (station.z_le - root.z_le) * scale,
      })),
    };
  }

  if (field === "sweepDeg") {
    const slope = Math.tan(radians(value));
    return {
      ...wing,
      stations: wing.stations.map((station) => ({
        ...station,
        x_le: root.x_le + (station.y - root.y) * slope,
      })),
    };
  }

  if (field === "dihedralDeg") {
    const slope = Math.tan(radians(value));
    return {
      ...wing,
      stations: wing.stations.map((station) => ({
        ...station,
        z_le: root.z_le + (station.y - root.y) * slope,
      })),
    };
  }

  const rootTwist = root.twist_deg;
  return {
    ...wing,
    stations: wing.stations.map((station) => {
      const eta = (station.y - root.y) / oldSpan;
      return { ...station, twist_deg: rootTwist + eta * (value - rootTwist) };
    }),
  };
}

export function naca4Shape(airfoil: string): Naca4Shape {
  const code = airfoil.trim().replace(/^naca[\s_-]*/i, "");
  if (!/^\d{4}$/.test(code)) {
    return { camberPercent: 0, camberPositionTenths: 0, thicknessPercent: 12 };
  }
  return {
    camberPercent: Number(code[0]),
    camberPositionTenths: Number(code[1]),
    thicknessPercent: Number(code.slice(2)),
  };
}

export function formatNaca4(shape: Naca4Shape): string {
  const camberPercent = Math.max(0, Math.min(9, Math.round(shape.camberPercent)));
  const camberPositionTenths = camberPercent === 0
    ? 0
    : Math.max(0, Math.min(9, Math.round(shape.camberPositionTenths)));
  const thicknessPercent = Math.max(1, Math.min(40, Math.round(shape.thicknessPercent)));
  return `NACA ${camberPercent}${camberPositionTenths}${thicknessPercent.toString().padStart(2, "0")}`;
}

export function setStationNacaShape(
  wing: WingDefinition,
  stationId: string,
  patch: Partial<Naca4Shape>,
): WingDefinition {
  const station = wing.stations.find((candidate) => candidate.id === stationId);
  if (!station) return wing;
  const next = formatNaca4({ ...naca4Shape(station.airfoil), ...patch });
  return replaceStation(wing, stationId, { ...station, airfoil: next });
}

export function setStationField(
  wing: WingDefinition,
  stationId: string,
  field: "y" | "x_le" | "z_le" | "chord" | "twist_deg",
  rawValue: number,
): WingDefinition {
  const index = wing.stations.findIndex((station) => station.id === stationId);
  if (index < 0) return wing;
  const station = wing.stations[index]!;
  let value = finite(rawValue, station[field]);
  if (field === "chord") value = Math.max(0.05, value);
  if (field === "y") {
    const previous = wing.stations[index - 1];
    const next = wing.stations[index + 1];
    if (!previous || !next) return wing;
    value = Math.max(previous.y + 0.01, Math.min(next.y - 0.01, value));
  }
  return replaceStation(wing, stationId, { ...station, [field]: value });
}

export function linearValues(min: number, max: number, levels: number): number[] {
  const count = Math.max(1, Math.round(levels));
  if (count === 1) return [min];
  return Array.from({ length: count }, (_, index) => min + (max - min) * index / (count - 1));
}

/** Endpoint-inclusive alpha samples, matching the backend planner exactly. */
export function alphaValues(min: number, max: number, rawSamples: number): number[] {
  const start = Math.min(min, max);
  const stop = Math.max(min, max);
  const count = Math.min(101, Math.max(2, Math.round(rawSamples)));
  return linearValues(start, stop, count).map((value) => Number(value.toPrecision(12)));
}

export function logarithmicValues(min: number, max: number, levels: number): number[] {
  const lo = Math.max(1, Math.min(min, max));
  const hi = Math.max(lo, Math.max(min, max));
  const count = Math.max(1, Math.round(levels));
  if (count === 1 || lo === hi) return [lo];
  const ratio = Math.pow(hi / lo, 1 / (count - 1));
  return Array.from({ length: count }, (_, index) => lo * Math.pow(ratio, index));
}

export function buildSectionCampaign(controls: SectionCampaignControls): SectionCampaignPlan {
  const alpha = alphaValues(controls.alphaMin, controls.alphaMax, controls.alphaSamples);
  const groupCount = Math.min(10_000, Math.max(1, Math.round(controls.baseDesignPoints)));
  const groups = Array.from({ length: groupCount }, (_, index): SectionCampaignGroup => ({
    id: `halton-${index + 1}`,
    designIndex: index,
    alphaValues: alpha,
    rowCount: alpha.length,
  }));
  const batchSize = 12;
  const haltonBatches = Array.from({ length: Math.ceil(groupCount / batchSize) }, (_, index) => {
    const firstGroup = index * batchSize + 1;
    const lastGroup = Math.min(groupCount, firstGroup + batchSize - 1);
    const count = lastGroup - firstGroup + 1;
    return { firstGroup, lastGroup, groupCount: count, rowCount: count * alpha.length };
  });
  return {
    alphaValues: alpha,
    groups,
    haltonBatches,
    groupedAlphaSweeps: groups.length,
    estimatedRows: groups.length * alpha.length,
  };
}
